import { useCallback, useEffect, useState } from "react";
import { theme, card, btnPrimary, btnSecondary, btnDanger, inputStyle, type as text } from "../theme";
import { fmtDate } from "../lib/format";
import { api, apiErrorMessage } from "../api/client";
import { DetailFields, EditButton } from "./RecordView";

// The zone-by-zone flow matrix: what the organisation says may pass from one zone (row) to another
// (column), set against what the dependency graph shows does pass. Rules in lib/networkZones.ts
// (flowFindings): a used pair nobody declared is undeclared, never allowed; a used pair declared
// deny contradicts the declaration; an allowed path into a more trusted zone needs a reason.
//
// A cell opens a read-only view of that pair (the declaration, the dependencies crossing it, the
// findings); declaring or changing it is behind Edit, for people who may edit.

interface MatrixZone { id: string; name: string; purpose: string; trust: number }
interface Flow {
  id: string; fromZoneId: string; toZoneId: string; decision: string;
  protocols: string | null; justification: string | null; reviewedAt: string | null;
}
interface Crossing { fromZoneId: string; toZoneId: string; dependencyIds: string[]; assetIds: string[] }
interface FlowFinding { id: string; kind: string; severity: string; title: string; zoneIds: string[] }
interface MatrixData { decisions: string[]; zones: MatrixZone[]; flows: Flow[]; crossings: Crossing[]; findings: FlowFinding[] }

const DECISION_STYLE: Record<string, { label: string; color: string; bg: string }> = {
  allow: { label: "Allow", color: theme.color.successText, bg: theme.color.successBg },
  restricted: { label: "Restricted", color: theme.color.warningText, bg: theme.color.warningBg },
  deny: { label: "Deny", color: theme.color.dangerText, bg: theme.color.dangerBg },
};

const key = (from: string, to: string) => `${from}>${to}`;

export function ZoneFlowMatrix({ canEdit, reloadKey, onChanged, focusZoneId }: {
  canEdit: boolean;
  /** Bumped by the page when zones or placements change, so the matrix reloads. */
  reloadKey?: number;
  onChanged?: () => void;
  /** Highlight one zone's row and column. */
  focusZoneId?: string | null;
}) {
  const [data, setData] = useState<MatrixData | null>(null);
  const [editing, setEditing] = useState<{ from: MatrixZone; to: MatrixZone } | null>(null);
  const [mode, setMode] = useState<"view" | "edit">("view");
  const [form, setForm] = useState({ decision: "allow", protocols: "", justification: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try { setData(await api.get<MatrixData>("/estate/zone-flows")); }
    catch (e) { setError(apiErrorMessage(e, "Could not load the zone flows.")); }
  }, []);
  useEffect(() => { void load(); }, [load, reloadKey]);

  if (!data) return error ? <div style={{ ...card, ...text.hint, color: theme.color.dangerText }}>{error}</div> : null;
  if (data.zones.length < 2) {
    return (
      <div style={{ ...card, ...text.hint }}>
        The flow matrix appears once there are two zones: it records what may pass from each zone to each other.
      </div>
    );
  }

  const flows = new Map(data.flows.map((f) => [key(f.fromZoneId, f.toZoneId), f]));
  const crossings = new Map(data.crossings.map((c) => [key(c.fromZoneId, c.toZoneId), c]));
  const findingsFor = (from: string, to: string) => data.findings.filter((f) => f.zoneIds[0] === from && f.zoneIds[1] === to);
  const current = editing ? flows.get(key(editing.from.id, editing.to.id)) ?? null : null;

  function openCell(from: MatrixZone, to: MatrixZone) {
    const f = flows.get(key(from.id, to.id));
    setForm({ decision: f?.decision ?? "allow", protocols: f?.protocols ?? "", justification: f?.justification ?? "" });
    setEditing({ from, to });
    setMode("view");
    setError("");
  }

  function startEdit() {
    if (!editing) return;
    const f = flows.get(key(editing.from.id, editing.to.id));
    setForm({ decision: f?.decision ?? "allow", protocols: f?.protocols ?? "", justification: f?.justification ?? "" });
    setError("");
    setMode("edit");
  }

  async function save() {
    if (!editing) return;
    setBusy(true); setError("");
    try {
      await api.put("/estate/zone-flows", {
        fromZoneId: editing.from.id, toZoneId: editing.to.id, decision: form.decision,
        protocols: form.protocols.trim() || null, justification: form.justification.trim() || null,
      });
      await load();
      setMode("view");
      onChanged?.();
    } catch (e) { setError(apiErrorMessage(e, "Could not save the flow.")); } finally { setBusy(false); }
  }

  async function clear() {
    if (!current) return;
    setBusy(true); setError("");
    try {
      await api.del(`/estate/zone-flows/${current.id}`);
      await load();
      setMode("view");
      onChanged?.();
    } catch (e) { setError(apiErrorMessage(e, "Could not remove the flow.")); } finally { setBusy(false); }
  }

  const inward = editing ? editing.from.trust > editing.to.trust : false;
  const cellBorder = `1px solid ${theme.color.border}`;

  return (
    <div style={card}>
      <div style={{ ...text.sectionTitle, marginBottom: 2 }}>Zone flows</div>
      <div style={{ ...text.hint, marginBottom: 10 }}>
        Rows are where traffic starts, columns where it goes. The number is how many dependencies in
        the asset graph cross that pair. An empty cell that is used is undeclared, never allowed.
        {canEdit ? " Click a cell to see what is declared for it, and to declare or review it." : " Click a cell to see what is declared for it."}
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", fontSize: text.meta.fontSize, fontFamily: theme.font }}>
          <thead>
            <tr>
              <th style={{ padding: 6, textAlign: "left", color: theme.color.faint, fontWeight: 600 }}>From / to</th>
              {data.zones.map((z) => (
                <th key={z.id} title={`Trust ${z.trust} (0 is most trusted)`} style={{
                  padding: 6, border: cellBorder, color: theme.color.heading, fontWeight: 600, minWidth: 90, maxWidth: 140,
                  background: focusZoneId === z.id ? theme.color.accentBg : undefined,
                }}>{z.name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.zones.map((from) => (
              <tr key={from.id}>
                <th style={{
                  padding: 6, border: cellBorder, textAlign: "left", color: theme.color.heading, fontWeight: 600, whiteSpace: "nowrap",
                  background: focusZoneId === from.id ? theme.color.accentBg : undefined,
                }}>{from.name}</th>
                {data.zones.map((to) => {
                  if (from.id === to.id) return <td key={to.id} style={{ border: cellBorder, background: theme.color.panelAlt }} />;
                  const flow = flows.get(key(from.id, to.id));
                  const crossing = crossings.get(key(from.id, to.id));
                  const cellFindings = findingsFor(from.id, to.id);
                  const worst = cellFindings.some((f) => f.severity === "high") ? "high" : cellFindings.length ? "medium" : null;
                  const style = flow ? DECISION_STYLE[flow.decision] : null;
                  const selected = editing?.from.id === from.id && editing?.to.id === to.id;
                  return (
                    <td key={to.id} style={{ border: cellBorder, padding: 0 }}>
                      <button
                        type="button"
                        onClick={() => openCell(from, to)}
                        title={[
                          flow ? `${style?.label ?? flow.decision}${flow.protocols ? ` — ${flow.protocols}` : ""}` : "Not declared",
                          crossing ? `${crossing.dependencyIds.length} dependenc${crossing.dependencyIds.length === 1 ? "y crosses" : "ies cross"} this pair` : "",
                          ...cellFindings.map((f) => f.title),
                        ].filter(Boolean).join("\n")}
                        style={{
                          width: "100%", minHeight: 38, padding: "4px 6px", fontFamily: theme.font, fontSize: text.meta.fontSize,
                          cursor: "pointer", textAlign: "center",
                          background: style?.bg ?? "transparent", color: style?.color ?? theme.color.faint,
                          border: selected ? `2px solid ${theme.color.accent}` : worst ? `2px solid ${worst === "high" ? theme.color.danger : theme.color.warning}` : "2px solid transparent",
                        }}
                      >
                        <div style={{ fontWeight: 700 }}>{style?.label ?? (crossing ? "Undeclared" : "—")}</div>
                        {crossing && <div style={{ color: theme.color.subtle }}>{crossing.dependencyIds.length} used</div>}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (() => {
        const crossing = crossings.get(key(editing.from.id, editing.to.id));
        const style = current ? DECISION_STYLE[current.decision] : null;
        return (
          <div style={{ marginTop: 12, padding: 12, border: `1px solid ${theme.color.border}`, borderRadius: 3 }}>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, marginBottom: 6, flexWrap: "wrap" }}>
              <div style={text.itemTitle}>
                {editing.from.name} → {editing.to.name}
                {inward && <span style={{ fontWeight: 400, color: theme.color.warningText }}> · into a more trusted zone</span>}
              </div>
              {canEdit && mode === "view" && <EditButton onClick={startEdit} label={current ? "Edit" : "Declare"} />}
            </div>
            {findingsFor(editing.from.id, editing.to.id).map((f) => (
              <div key={f.id} style={{ ...text.hint, color: f.severity === "high" ? theme.color.dangerText : theme.color.warningText, marginBottom: 4 }}>{f.title}</div>
            ))}
            {mode === "view" ? (
              <>
                <DetailFields fields={[
                  { label: "Decision", value: current ? (style?.label ?? current.decision) : "Not declared" },
                  { label: "Protocols and ports", value: current?.protocols },
                  { label: "Dependencies crossing this pair", value: crossing ? String(crossing.dependencyIds.length) : "None" },
                  { label: "Last reviewed", value: current?.reviewedAt ? fmtDate(current.reviewedAt) : null },
                  { label: "Justification", value: current?.justification, wide: true, multiline: true },
                ]} />
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
                  <button type="button" onClick={() => setEditing(null)} style={btnSecondary}>Close</button>
                </div>
              </>
            ) : (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 10 }}>
                  <label style={text.label}>
                    Decision
                    <select value={form.decision} onChange={(e) => setForm({ ...form, decision: e.target.value })} style={{ ...inputStyle, marginTop: 4 }}>
                      {data.decisions.map((d) => <option key={d} value={d}>{DECISION_STYLE[d]?.label ?? d}</option>)}
                    </select>
                  </label>
                  <label style={text.label}>
                    Protocols and ports
                    <input value={form.protocols} onChange={(e) => setForm({ ...form, protocols: e.target.value })} placeholder="e.g. HTTPS 443, OPC UA 4840" style={{ ...inputStyle, marginTop: 4 }} />
                  </label>
                  <label style={{ ...text.label, gridColumn: "1 / -1" }}>
                    Justification{inward && form.decision !== "deny" ? " (needed: this runs into a more trusted zone)" : ""}
                    <textarea value={form.justification} onChange={(e) => setForm({ ...form, justification: e.target.value })} rows={2} placeholder="The business reason and the control that mediates it — a firewall rule, a broker, a data diode" style={{ ...inputStyle, marginTop: 4, resize: "vertical" }} />
                  </label>
                </div>
                {current?.reviewedAt && <div style={{ ...text.meta, marginTop: 6 }}>Last reviewed {fmtDate(current.reviewedAt)}. Saving records a new review.</div>}
                {error && <div style={{ ...text.hint, color: theme.color.dangerText, marginTop: 6 }}>{error}</div>}
                <div style={{ display: "flex", gap: 8, marginTop: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
                  {current && <button type="button" onClick={clear} disabled={busy} style={{ ...btnDanger, marginRight: "auto" }}>Remove declaration</button>}
                  <button type="button" onClick={() => { setMode("view"); setError(""); }} style={btnSecondary}>Cancel</button>
                  <button type="button" onClick={save} disabled={busy} style={{ ...btnPrimary, opacity: busy ? 0.6 : 1 }}>Save</button>
                </div>
              </>
            )}
          </div>
        );
      })()}
    </div>
  );
}
